import _ from 'underscore';
import { Message, Activity } from 'botframework-directlinejs';
import { type BotActivity } from '@bingads-webui/ads-copilot-shared-types';
import { I18nModel } from '@bingads-webui/copilot-i18n';

/**
 * Used by the UI to signal user intent to the AI. Returned by AI as the Name property of plugin results.
 * @see https://msasg.visualstudio.com/Bing_Ads/_git/AdsAppsMT?path=/private/Campaign/MT/Source/AdsCopilot/AdsCopilotMT.Library/Model/PluginScenarios.cs
 */
export enum PluginScenarios {
  Summarize = 'Summarize',
  AccountFluctuation = 'AccountFluctuation',
  CampaignFluctuation = 'CampaignFluctuation',
  PerformanceTimeSeries = 'PerformanceTimeSeries',
}

export const PluginScenarioTroubleshootingDetection = 'AdDeliveryTroubleShooter';

export const MessageActivityClassName = 'message-activity';
export const AutoScrollerContentClassName = 'auto-scroller-content';
export const AIMessageClassName = 'ai-message';
export const ForceIntoVisibilityClassName = 'force-into-visibility';
export const ActivityGroupContainerClassName = 'activity-group-container';

// Whether to show the active conversation id inside the Copilot panel.
// Returns true when `?copilotchatdebug=1` (or `=true`, case-insensitive key)
// is on the URL. Same affordance MSACopilot uses; lives here (rather than
// msa-copilot-common) so AdsCopilot can import without a circular dep —
// msa-copilot-common already depends on ads-copilot-ui.
export const getShouldRenderConversationId = (searchParams: URLSearchParams): boolean => {
  const v = searchParams.get('copilotchatdebug') ?? searchParams.get('copilotChatDebug');
  return v === '1' || v === 'true';
};

/**
 * Returned by the bot in value.Features array to signal which plugin functions were invoked.
 */
export enum PluginFunctions {
  SummarizeCampaignPerformance = 'SummarizeCampaignPerformance',
  SummarizeCampaignPerformanceV2 = 'SummarizeCampaignPerformanceV2',
  SummarizeCampaignPerformanceV3 = 'SummarizeCampaignPerformanceV3',
  GetAccountFluctuations = 'GetAccountFluctuations',
  GetCampaignFluctuations = 'GetCampaignFluctuations',
  CampaignPerformanceTimeSeries = 'CampaignPerformanceTimeSeries',
  CampaignPerformanceTimeSeriesV2 = 'CampaignPerformanceTimeSeriesV2',
  GetAdvertiserAccountDetails = 'GetAdvertiserAccountDetails',
  RankEntity = 'RankEntity',
  ShiftDiagnosis = 'ShiftDiagnosis',
  TestYourTagTool = 'TestYourTagTool',
}

export enum IntentType {
  None = 'None',
  ConnectAgent = 'ConnectAgent',
  AdsPreview = 'AdsPreview',
  SignUpLink = 'SignUpLink',
  SignInLink = 'SignInLink',
  TestYourTag = 'TestYourTagTool',
}

export enum AgentQueue {
  Support = 'Support',
  Sales = 'Sales',
  None = 'None',
}

export const CATEGORY_TROUBLESHOOTING = 'troubleshooting';
export const TROUBLESHOOTING_AD_DELIVERY = 'AdDelivery';

export const EVENT_NAME_ENGAGE_TROUBLESHOOTER = 'engageTroubleshooter';
export const EVENT_NAME_PLUGIN = 'pluginResults';
export const EVENT_NAME_LIVE_AGENT_CHAT = 'liveAgentChat';
export const REACTION_TYPE_ACCOUNT_SWITCHED = 'AccountSwitched';

export function isAccountSwitchReaction(activity: Activity): boolean {
  return (activity.type as string) === 'messageReaction'
    && Array.isArray((activity as any).reactionsAdded)
    && (activity as any).reactionsAdded.some((r: any) => r.type === REACTION_TYPE_ACCOUNT_SWITCHED);
}

export const isTypingMessage = (activity: BotActivity & Message) => activity?.text === 'StaticMessages' && (activity as BotActivity).value === 'Typing';

// 1. engage troubleshooting event
// 2. ad delivery plugin event from MT side
export function isTroubleshootingEvent(activity: Activity) {
  const isEngageEvent = activity.type === 'event'
    && activity.name === EVENT_NAME_ENGAGE_TROUBLESHOOTER;

  const isAdDeliveryPluginEvent = activity.type === 'event'
    && activity.name === EVENT_NAME_PLUGIN
    && _.any((activity as any).value, pluginResult => pluginResult.Name === PluginScenarioTroubleshootingDetection);

  return isEngageEvent || isAdDeliveryPluginEvent;
}

export const getIsFluctuationsFeature = (activity: any) => {
  const features = (activity.value as any)?.Features;

  return _.any(features, feature => (feature === PluginFunctions.GetAccountFluctuations
    || feature === PluginFunctions.GetCampaignFluctuations));
};

export const getIsAdvertiserAccountDetailsFeature = (activity: any) => {
  const features = (activity.value as any)?.Features;

  return _.any(features, feature => (feature === PluginFunctions.GetAdvertiserAccountDetails));
};

export const getIsTestYourTagEvent = (activity: any) => {
  if (activity.type === 'message') {
    return _.any(activity.value?.IntentData, intent => intent.IntentType === IntentType.TestYourTag);
  }
  return false;
};

export const shouldFeatureShowDisclaimer = (activity: any) => {
  const featuresWithDisclaimer = [
    PluginFunctions.GetAdvertiserAccountDetails,
    PluginFunctions.GetAccountFluctuations,
    PluginFunctions.GetCampaignFluctuations,
    PluginFunctions.SummarizeCampaignPerformance,
    PluginFunctions.SummarizeCampaignPerformanceV2,
    PluginFunctions.SummarizeCampaignPerformanceV3,
    PluginFunctions.CampaignPerformanceTimeSeries,
    PluginFunctions.CampaignPerformanceTimeSeriesV2,
    PluginFunctions.RankEntity,
    PluginFunctions.ShiftDiagnosis,
  ];
  const activityFeatures = (activity.value as any)?.Features;

  return !_.isEmpty(_.intersection(featuresWithDisclaimer, activityFeatures));
};

export const getDisclaimerString = (activity: any) => {
  const features = (activity.value as any)?.Features;
  if (_.any(features, feature => (feature === PluginFunctions.GetAdvertiserAccountDetails))) {
    return _TL_('Copilot answers questions using Microsoft Advertising\'s data for your account.');
  }

  return _TL_('Copilot answers questions about performance using Microsoft Advertising\'s reporting data for both your enabled and paused campaigns.');
};

export function getIsFromUser(activity: Activity, username: string): boolean {
  return activity.from.name === username || activity.from.name === 'USERNAME';
}

export function getIsGeneratingAnswersAnimation(activity: any): boolean {
  return activity?.value === 'AnimationCard' && activity?.attachments?.[0]?.content?.text === 'GeneratingAnswers';
}

export const getPrivacyStatementLink = (i18n: I18nModel | null) => `https://privacy.microsoft.com/${i18n?.getLocale() || 'en-us'}/privacystatement`;
